/* =============================================================================
 * M.M.LaFleur — PDP gallery layout modes.
 *
 * Four layouts, chosen per-template in the theme editor and published onto the
 * gallery as data-layout / data-layout-mobile:
 *
 *   grid      two-up masonry-ish grid (the original, and the default)
 *   stack     one full-width frame per row
 *   carousel  horizontal scroller with arrows
 *   rail      one large stage plus a thumbnail rail
 *
 * WHY THERE IS NO WRAPPER ELEMENT
 *
 * assets/mm-gallery-dedupe.js removes duplicate .mm-gallery-item nodes and
 * re-runs on every DOM mutation through a MutationObserver. Anything this file
 * injected *inside* .mm-gallery would be re-examined on a 60ms debounce, and a
 * thumbnail carrying a copy of an existing photograph would be deleted as a
 * duplicate of it.
 *
 * So: the carousel is pure CSS scroll-snap on .mm-gallery itself, driven by
 * scrollBy — no track element, no cloning, no mutation of the gallery's
 * children at all. The rail is built as a *sibling* of .mm-gallery, which the
 * deduper never looks at.
 *
 * Ordering also matters. The deduper runs on DOMContentLoaded and may remove
 * frames; a rail built before it finishes would carry thumbnails for images
 * that no longer exist. This file therefore builds on the next frame after
 * DOMContentLoaded and rebuilds when the gallery's child count changes.
 *
 * NOTE: a dots indicator for the mobile carousel briefly lived here and was
 * removed — the deeper frame peek carries the same message on its own, and the
 * dots were another element competing for the space between the gallery and the
 * product name. buildRail below is the remaining indicator, used by the rail
 * layout only.
 *
 * OTHER PDP ENHANCEMENTS. Two unrelated IIFEs at the bottom — long size labels
 * and partial rating stars. They live here only because this is the one small
 * script sections/mm_product.liquid already loads, and that section (70KB) is
 * too large to rewrite safely for markup tweaks. If the section is ever
 * rewritten, move both server-side and delete them from here.
 * ========================================================================== */

(function () {
  "use strict";

  var MOBILE = "(max-width: 767px)";

  function items(gallery) {
    return Array.prototype.slice.call(
      gallery.querySelectorAll(":scope > .mm-gallery-item")
    );
  }

  /* The layout actually in force, which is not always the one the desktop
     setting asked for. */
  function activeLayout(gallery) {
    var mobile = gallery.getAttribute("data-layout-mobile");
    if (mobile && window.matchMedia(MOBILE).matches) return mobile;
    return gallery.getAttribute("data-layout") || "grid";
  }

  /* One rAF-throttled scroll listener per consumer. */
  function onScroll(gallery, fn) {
    var ticking = false;
    gallery.addEventListener("scroll", function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { fn(); ticking = false; });
    }, { passive: true });
  }

  /* --------------------------------------------------------------------- */
  /* Arrows                                                                 */
  /* --------------------------------------------------------------------- */

  function stepFor(gallery) {
    var first = gallery.querySelector(":scope > .mm-gallery-item");
    if (!first) return gallery.clientWidth;
    var style = window.getComputedStyle(gallery);
    var gap = parseFloat(style.columnGap || style.gap || "0") || 0;
    return first.getBoundingClientRect().width + gap;
  }

  function syncArrows(gallery, prev, next) {
    /* scrollWidth can exceed clientWidth by a sub-pixel amount at some zoom
       levels, which would leave both arrows enabled on a gallery that cannot
       actually scroll. */
    var slack = gallery.scrollWidth - gallery.clientWidth;
    if (slack < 2) {
      prev.hidden = true;
      next.hidden = true;
      return;
    }
    prev.hidden = false;
    next.hidden = false;
    prev.disabled = gallery.scrollLeft < 2;
    next.disabled = gallery.scrollLeft >= slack - 2;
  }

  function buildArrows(gallery, shell) {
    var prev = document.createElement("button");
    var next = document.createElement("button");

    prev.type = "button";
    next.type = "button";
    prev.className = "mm-gallery-arrow mm-gallery-arrow--prev";
    next.className = "mm-gallery-arrow mm-gallery-arrow--next";
    prev.setAttribute("aria-label", "Previous image");
    next.setAttribute("aria-label", "Next image");
    prev.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5 8 12l7 7"/></svg>';
    next.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 5 7 7-7 7"/></svg>';

    prev.addEventListener("click", function () {
      gallery.scrollBy({ left: -stepFor(gallery), behavior: "smooth" });
    });
    next.addEventListener("click", function () {
      gallery.scrollBy({ left: stepFor(gallery), behavior: "smooth" });
    });

    shell.appendChild(prev);
    shell.appendChild(next);

    onScroll(gallery, function () { syncArrows(gallery, prev, next); });

    syncArrows(gallery, prev, next);
    return function () { syncArrows(gallery, prev, next); };
  }

  /* --------------------------------------------------------------------- */
  /* Thumbnail rail                                                         */
  /* --------------------------------------------------------------------- */

  function buildRail(gallery, shell) {
    var frames = items(gallery);
    if (frames.length < 2) return null;

    var rail = document.createElement("div");
    rail.className = "mm-gallery-rail";
    rail.setAttribute("role", "tablist");
    rail.setAttribute("aria-label", "Product images");

    var thumbs = frames.map(function (frame, i) {
      var button = document.createElement("button");
      button.type = "button";
      button.className = "mm-gallery-thumb";
      button.setAttribute("role", "tab");
      button.setAttribute("aria-label", "View image " + (i + 1));

      var source = frame.querySelector("img");
      var poster = frame.querySelector("video");

      if (source) {
        var thumb = document.createElement("img");
        /* currentSrc where the browser has already resolved the srcset, so the
           thumbnail reuses a rendition that is very likely cached. */
        thumb.src = source.currentSrc || source.src;
        thumb.alt = "";
        thumb.loading = "lazy";
        thumb.decoding = "async";
        button.appendChild(thumb);
      } else if (poster && poster.poster) {
        var still = document.createElement("img");
        still.src = poster.poster;
        still.alt = "";
        still.loading = "lazy";
        button.appendChild(still);
        button.setAttribute("data-mm-video", "true");
      } else {
        button.textContent = String(i + 1);
      }

      button.addEventListener("click", function () {
        gallery.scrollTo({ left: frame.offsetLeft - gallery.offsetLeft, behavior: "smooth" });
      });

      rail.appendChild(button);
      return button;
    });

    shell.appendChild(rail);

    /* Which frame is on the stage, worked out from scroll position rather than
       from the last click — the stage is swipeable too. */
    function mark() {
      var mid = gallery.scrollLeft + gallery.clientWidth / 2;
      var best = 0;
      var bestGap = Infinity;
      frames.forEach(function (frame, i) {
        var centre = frame.offsetLeft - gallery.offsetLeft + frame.offsetWidth / 2;
        var gap = Math.abs(centre - mid);
        if (gap < bestGap) { bestGap = gap; best = i; }
      });
      thumbs.forEach(function (t, i) {
        t.setAttribute("aria-selected", i === best ? "true" : "false");
      });
    }

    onScroll(gallery, mark);
    mark();
    return mark;
  }

  /* --------------------------------------------------------------------- */

  function teardown(shell) {
    Array.prototype.forEach.call(
      shell.querySelectorAll(".mm-gallery-arrow, .mm-gallery-rail, .mm-gallery-dots"),
      function (node) { node.parentNode.removeChild(node); }
    );
  }

  function build(shell) {
    var gallery = shell.querySelector(".mm-gallery");
    if (!gallery) return;

    teardown(shell);

    var layout = activeLayout(gallery);
    shell.setAttribute("data-active-layout", layout);

    if (layout !== "carousel" && layout !== "rail") {
      /* Grid and stack scroll the page, not the gallery. Reset any offset left
         behind by a resize out of a scrolling layout. */
      gallery.scrollLeft = 0;
      shell.__mmSync = null;
      return;
    }

    var syncers = [];
    var arrows = buildArrows(gallery, shell);
    if (arrows) syncers.push(arrows);

    if (layout === "rail") {
      var mark = buildRail(gallery, shell);
      if (mark) syncers.push(mark);
    }

    shell.__mmSync = function () {
      syncers.forEach(function (fn) { fn(); });
    };
  }

  function shells() {
    return Array.prototype.slice.call(
      document.querySelectorAll("[data-mm-gallery-shell]")
    );
  }

  function run() {
    shells().forEach(function (shell) {
      var gallery = shell.querySelector(".mm-gallery");
      if (!gallery) return;

      /* Rebuild only when the frame count changed — which is exactly what the
         deduper does to us — or when the layout in force changed, which is what
         crossing the mobile breakpoint does. Anything else and we would rebuild
         on our own mutations forever. */
      var signature = items(gallery).length + ":" + activeLayout(gallery);
      if (shell.__mmSignature === signature) {
        if (shell.__mmSync) shell.__mmSync();
        return;
      }
      shell.__mmSignature = signature;
      build(shell);
    });
  }

  function start() {
    /* One frame after DOMContentLoaded, so mm-gallery-dedupe.js has finished
       removing frames before any thumbnail is made from one. */
    window.requestAnimationFrame(run);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  var resizePending = null;
  window.addEventListener("resize", function () {
    if (resizePending) window.clearTimeout(resizePending);
    resizePending = window.setTimeout(run, 150);
  }, { passive: true });

  /* Barba swaps the main container; the deduper watches for the same thing. */
  var mutationPending = null;
  new MutationObserver(function () {
    if (mutationPending) window.clearTimeout(mutationPending);
    mutationPending = window.setTimeout(run, 120);
  }).observe(document.body, { childList: true, subtree: true });
}());

/* =============================================================================
 * PDP SIZE LABELS (Sept 2026)
 *
 * Shoe sizes arrive as "IT 42.5 - US 11.5 to 12" and overflowed the fixed-height
 * 6-column size grid. This splits "A - B" labels into two lines after render and
 * flags the group data-mm-long when any label is over 5 characters, which
 * switches the grid to wider auto-fill columns. Letter/number sizes are
 * untouched.
 *
 * Quick add does the same split server-side in templates/product.mm-quickadd.
 * mm-editorial.js never reads a size button's text, so replacing it with spans
 * is safe; the full label is kept on aria-label.
 *
 * Without JS the grid still grows to fit (height:auto), so nothing overlaps.
 * ========================================================================== */

(function () {
  "use strict";

  var CSS =
    ".mm-scope .mm-pdp-sizes[data-mm-size-group] button{height:auto;min-height:2.75rem;padding:0.375rem 0.25rem;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;line-height:1.15}" +
    ".mm-scope .mm-pdp-sizes[data-mm-size-group][data-mm-long]{grid-template-columns:repeat(auto-fill,minmax(5.25rem,1fr))}" +
    ".mm-scope .mm-pdp-sizes[data-mm-size-group][data-mm-long] button{white-space:nowrap;overflow:hidden}" +
    ".mm-scope .mm-pdp-sizes[data-mm-size-group] .mm-size-a{font-size:0.8125rem}" +
    ".mm-scope .mm-pdp-sizes[data-mm-size-group] .mm-size-b{font-size:11px;opacity:0.65}";

  function ensureCss() {
    if (document.getElementById("mm-size-labels-css")) return;
    var style = document.createElement("style");
    style.id = "mm-size-labels-css";
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function split() {
    var groups = document.querySelectorAll(".mm-pdp-sizes[data-mm-size-group]:not([data-mm-labels])");
    if (!groups.length) return;
    ensureCss();

    Array.prototype.forEach.call(groups, function (group) {
      group.setAttribute("data-mm-labels", "1");
      var long = false;

      Array.prototype.forEach.call(group.querySelectorAll("[data-mm-size]"), function (btn) {
        if (btn.querySelector(".mm-size-a")) return;
        var label = (btn.textContent || "").replace(/\s+/g, " ").trim();
        if (label.length > 5) long = true;

        var parts = label.split(" - ");
        if (parts.length < 2) return;

        btn.setAttribute("aria-label", label);
        btn.textContent = "";

        var a = document.createElement("span");
        a.className = "mm-size-a";
        a.textContent = parts[0].trim();

        var b = document.createElement("span");
        b.className = "mm-size-b";
        b.textContent = parts.slice(1).join(" - ").trim().replace(/ to /g, "\u2013");

        btn.appendChild(a);
        btn.appendChild(b);
      });

      if (long) group.setAttribute("data-mm-long", "");
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", split);
  } else {
    split();
  }

  /* Barba swaps and theme-editor section reloads bring in fresh, unmarked
     groups; the :not([data-mm-labels]) guard keeps repeat runs cheap. */
  var pending = null;
  new MutationObserver(function () {
    if (pending) return;
    pending = window.requestAnimationFrame(function () { pending = null; split(); });
  }).observe(document.body, { childList: true, subtree: true });
}());

/* =============================================================================
 * PDP PARTIAL RATING STARS (Sept 2026)
 *
 * The section renders whole stars only (`i <= rating_value`), so 4.6 showed as
 * four filled and one empty. This rebuilds .mm-pdp-rating .mm-stars so each
 * star is the empty icon with the filled icon laid over it, clipped to the
 * fraction that star earns: 4.6 -> 1, 1, 1, 1, 0.6.
 *
 * The icons are cloned from what the section already rendered — the first
 * star is the filled icon, the last is the empty one — so the look stays
 * whatever snippets/mm_icon.liquid draws. That needs at least one of each, so
 * a rating under 1.0 or exactly 5.0 is left as rendered (both already correct
 * or near enough).
 *
 * The value comes from the "4.6 · 450 reviews" label beside the stars, which
 * the section prints from the same metafield.
 * ========================================================================== */

(function () {
  "use strict";

  var CSS =
    ".mm-scope .mm-stars .mm-star-cell{position:relative;display:inline-block;line-height:0}" +
    ".mm-scope .mm-stars .mm-star-cell > svg{display:block}" +
    ".mm-scope .mm-stars .mm-star-fill{position:absolute;top:0;left:0;bottom:0;overflow:hidden;line-height:0}" +
    ".mm-scope .mm-stars .mm-star-fill > svg{display:block;max-width:none}";

  function ensureCss() {
    if (document.getElementById("mm-partial-stars-css")) return;
    var style = document.createElement("style");
    style.id = "mm-partial-stars-css";
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function paint() {
    var links = document.querySelectorAll(".mm-pdp-rating:not([data-mm-partial])");
    if (!links.length) return;

    Array.prototype.forEach.call(links, function (link) {
      link.setAttribute("data-mm-partial", "1");

      var box = link.querySelector(".mm-stars");
      var label = link.querySelector(":scope > span:not(.mm-stars)");
      if (!box || !label) return;

      var match = (label.textContent || "").match(/(\d+(?:\.\d+)?)/);
      if (!match) return;
      var value = Math.max(0, Math.min(5, parseFloat(match[1])));

      var icons = box.querySelectorAll(":scope > svg");
      if (icons.length !== 5) return;

      var whole = Math.floor(value);
      if (whole < 1 || whole >= 5 || value === whole) return;

      var filled = icons[0];
      var empty = icons[4];

      ensureCss();
      box.setAttribute("role", "img");
      box.setAttribute("aria-label", "Rated " + value + " out of 5");
      box.textContent = "";

      for (var i = 0; i < 5; i += 1) {
        var share = Math.max(0, Math.min(1, value - i));
        var cell = document.createElement("span");
        cell.className = "mm-star-cell";
        cell.setAttribute("aria-hidden", "true");
        cell.appendChild(empty.cloneNode(true));

        if (share > 0) {
          var fill = document.createElement("span");
          fill.className = "mm-star-fill";
          fill.style.width = (share * 100).toFixed(1) + "%";
          fill.appendChild(filled.cloneNode(true));
          cell.appendChild(fill);
        }

        box.appendChild(cell);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", paint);
  } else {
    paint();
  }

  var pending = null;
  new MutationObserver(function () {
    if (pending) return;
    pending = window.requestAnimationFrame(function () { pending = null; paint(); });
  }).observe(document.body, { childList: true, subtree: true });
}());
